
import { Injectable } from '@angular/core';
import { Auth, OAuthProvider, User, user, signOut, signInWithRedirect } from '@angular/fire/auth';
import { Observable, firstValueFrom, take } from 'rxjs';
import { environment } from '../../environments/environment';
import { browserSessionPersistence, setPersistence } from 'firebase/auth';

interface ExtendedUser {
  stsTokenManager: {
    accessToken: string;
    refreshToken: string;
    expiresIn: string;
  };
  
}


interface DecodedToken {
  iss: string;
  aud: string;
  auth_time: number;
  user_id: string;
  sub: string;
  iat: number;
  exp: number;
  firebase: {
    identities: {
      [key: string]: string[];
    };
    sign_in_provider: string;
    sign_in_attributes: {
      at_hash: string;
      rt_hash: string;
      groups: string[];
    };
  };
}


@Injectable({
  providedIn: 'root'
})
export class AuthService {
  OIDC_PROVIDER = environment.OIDC_PROVIDER;
  loginUserDetails: any;
  user: User | undefined
  user$: Observable<User | null>;
  constructor(private auth: Auth) {
    this.user$ = user(this.auth);
    this.enableLogoutOnBrowserClose();
  } 

  /**
   * Automatically logout and clear session when browser closes
   */ 
  private enableLogoutOnBrowserClose() {
    window.addEventListener('beforeunload', async () => {
      try {
        await setTimeout(() =>this.signOut(),50);
;
      } catch (err) {
        console.error('Error signing out on close', err)
      }
    })
  }

  async getUserDetails(): Promise<DecodedToken> {
    return new Promise((resolve, reject) => {
      user(this.auth).pipe(take(1)).subscribe({
        next: (loggedInUser: any) => {
          if (loggedInUser?.accessToken) {
            const tokenDetails = this.decodeAccessToken(loggedInUser.accessToken);
            resolve(tokenDetails);
          } else {
            reject('User not logged in');
          }
        },
        error: (err) => reject(err)
      });
    });
  }

  decodeAccessToken(token: any) {        
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Invalid token format:', error);
      return null;
    }
  }

  
async signOut(): Promise<void> {
  try {
    await signOut(this.auth);
    sessionStorage.clear(); // Clear any stored tokens or user data
    localStorage.clear();
    console.log('User signed out successfully');
  } catch (error) {
    console.error('Error signing out:', error);
  }
}

async loginWithMic() {  
  try {
     await setPersistence(this.auth, browserSessionPersistence);
      const loggedInUser = await firstValueFrom(user(this.auth).pipe(take(1)));
      if (loggedInUser) {
        const extendedUser = loggedInUser as unknown as ExtendedUser;
        const accessToken = extendedUser.stsTokenManager.accessToken;
        const tokenPayload = this.decodeAccessToken(accessToken);
        return tokenPayload;
      } else {
        console.warn(`User is not logged in. Performing login.`);
        await this.oidcLogin(this.OIDC_PROVIDER); // This will redirect
        return null;
      }
    } catch (error) {
      console.error("Error occurred during loginWithMic() call", error);
      this.signOut();
      return null;
    }
}

  async oidcLogin(providerId: string) {
    const provider = new OAuthProvider(providerId);
    provider.addScope('groups');
    return signInWithRedirect(this.auth, provider);
  }

  /// Below code will be tested only after ISAM login event

  // async signInWithPopup(): Promise<boolean> {
  //   const provider = new OAuthProvider(this.SSO_PROVIDER);

  //   try {
  //     const result = await signInWithPopup(this.auth, provider);
  //     this.loginUserDetails = result;
  //     const userDetails = this.generateUserDetails(result);
  //     const isTokenExpired = this.isTokenExpired(userDetails.accessToken);
  //     sessionStorage.setItem('token', userDetails.accessToken);
  //     return !isTokenExpired;
  //   } catch (error: any) {
  //     console.error('Sign-in error:', error);
  //     return false;
  //   }
  // }

  // isTokenExpired(token: string): boolean {
  //   const base64Url = token.split('.')[1];
  //   const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  //   const jsonPayload = decodeURIComponent(
  //     atob(base64)
  //       .split('')
  //       .map(c => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
  //       .join('')
  //   );
  //   const { exp } = JSON.parse(jsonPayload);
  //   return Date.now() >= exp * 1000;
  // }
}
